import { useEffect, useMemo, useRef, useState } from 'react';
import { safeParseConfig } from '../../exercise-templates/blockConfig';
import { MATCHING_DEFAULT_CONFIG, MATCHING_SIDE_KIND_TEXT, MATCHING_MODE_DEFERRED, getMatchingMode, normalizePairs, isSideFilled } from '../../exercise-templates/matchingConfig';
import OrderingDnd from './OrderingDnd';

// Local-state-only "what will this look like to a student" previews - no
// correctness reveal, nothing persisted anywhere (the CRM has no student
// session to save into, and doesn't need one for this). The
// select-or-type types (multiple_choice/true_false/fill_blank) get real
// clickable/typeable controls, and matching (below) is a real click-to-pair
// game, and ordering is a drag-into-numbered-slots exercise (OrderingDnd.jsx).

function shuffle(list) {
    const copy = [...list];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

// A plain independent shuffle of both columns can coincidentally land a
// correct pair on the same row (visually pointing at the answer, no click
// needed) - rotating the already-shuffled left order by a random non-zero
// offset guarantees no row ever has its correct match directly across from
// it, while still looking like an independent, well-mixed order.
function rotateNoFixedPoints(list) {
    const n = list.length;
    if (n <= 1) return [...list];
    const offset = 1 + Math.floor(Math.random() * (n - 1));
    return list.map((_, i) => list[(i + offset) % n]);
}

const MC_DEFAULT = { question: '', options: [], correctIds: [], multiple: false };
export function MultipleChoicePreview({ block }) {
    const cfg = safeParseConfig(block.config, MC_DEFAULT);
    const options = (cfg.options || []).filter((o) => o.text && o.text.trim());
    const [picked, setPicked] = useState([]);

    const toggle = (id) => {
        setPicked((prev) => {
            if (cfg.multiple) return prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id];
            return [id];
        });
    };

    return (
        <div className='course-preview-question'>
            {cfg.question ? <p className='course-preview-question-text'>{cfg.question}</p> : ''}
            <div className='course-preview-options'>
                {options.map((o) => (
                    <label className='course-preview-option-row' key={o.id}>
                        <input type={cfg.multiple ? 'checkbox' : 'radio'} name={'mc-' + block.id}
                            checked={picked.includes(o.id)} onChange={() => toggle(o.id)} />
                        <span>{o.text}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

const TF_DEFAULT = { statement: '', correct: true };
export function TrueFalsePreview({ block }) {
    const cfg = safeParseConfig(block.config, TF_DEFAULT);
    const [picked, setPicked] = useState(null);

    return (
        <div className='course-preview-question'>
            {cfg.statement ? <p className='course-preview-question-text'>{cfg.statement}</p> : ''}
            <div className='course-preview-options'>
                <label className='course-preview-option-row'>
                    <input type='radio' name={'tf-' + block.id} checked={picked === true} onChange={() => setPicked(true)} />
                    <span>Правда</span>
                </label>
                <label className='course-preview-option-row'>
                    <input type='radio' name={'tf-' + block.id} checked={picked === false} onChange={() => setPicked(false)} />
                    <span>Хибність</span>
                </label>
            </div>
        </div>
    );
}

const FB_DEFAULT = { template: '', blanks: [] };
const BLANK_MARKER = '___';
export function FillBlankPreview({ block }) {
    const cfg = safeParseConfig(block.config, FB_DEFAULT);
    const parts = (cfg.template || '').split(BLANK_MARKER);
    const [values, setValues] = useState({});

    const handleChange = (i, value) => setValues((prev) => ({ ...prev, [i]: value }));

    return (
        <p className='course-preview-question-text course-preview-fill-blank'>
            {parts.map((part, i) => (
                <span key={i}>
                    {part}
                    {i < parts.length - 1 ? (
                        <input type='text' className='course-preview-blank-input'
                            value={values[i] || ''} onChange={(e) => handleChange(i, e.target.value)} />
                    ) : ''}
                </span>
            ))}
        </p>
    );
}

// Free-text response - the teacher's task description, then a large answer
// area sized to what the user asked for (full available width, ~400px
// tall). Local-state-only like every sibling here (typed text never
// persists from this admin simulator) - the STUDENT platform is the one
// that actually saves an answer, via the same generic
// ExerciseStudentSavedInteractiveElement.resultJson mechanism every other
// interactive type already uses, shape {text:"..."}.
const ESSAY_DEFAULT = { prompt: '' };
export function EssayPreview({ block }) {
    const cfg = safeParseConfig(block.config, ESSAY_DEFAULT);
    const [answer, setAnswer] = useState('');

    return (
        <div className='course-preview-essay'>
            {cfg.prompt ? <p className='course-preview-question-text'>{cfg.prompt}</p> : ''}
            <textarea className='course-preview-essay-textarea' value={answer}
                placeholder='Введіть вашу відповідь...' onChange={(e) => setAnswer(e.target.value)} />
        </div>
    );
}

// Same {prompt} config/task description as essay - only the answer
// MECHANISM differs, and recording only actually happens on the real
// student platform (this admin simulator has no reason to capture real
// audio). Decorative record affordance only, so the admin still sees
// "this is what the student will be asked to do" without a functional mic.
export function AudioResponsePreview({ block }) {
    const cfg = safeParseConfig(block.config, ESSAY_DEFAULT);
    return (
        <div className='course-preview-audio-response'>
            {cfg.prompt ? <p className='course-preview-question-text'>{cfg.prompt}</p> : ''}
            <div className='course-preview-audio-record-btn' title='Запис доступний лише на платформі студента'>
                <span className='course-preview-audio-record-dot' />
                Записати відповідь
            </div>
        </div>
    );
}

// Tap shuffled word chips in order to rebuild the sentence - same tap-not-
// drag interaction convention MatchingPreview already established for this
// file (no HTML5 drag events anywhere in this admin simulator). No
// correctness reveal here either, matching every sibling preview - the
// REAL graded view (with correct/wrong coloring) lives in the CRM
// workspace's AnsweredSentenceOrder, reviewing actual recorded activity.
const SENTENCE_ORDER_DEFAULT = { prompt: '', words: [] };
export function SentenceOrderPreview({ block }) {
    const cfg = safeParseConfig(block.config, SENTENCE_ORDER_DEFAULT);
    const words = useMemo(() => (cfg.words || []).map((w, i) => ({ text: w, id: i })), [cfg.words]);
    const [bank] = useState(() => shuffle(words));
    const [built, setBuilt] = useState([]);

    if (words.length === 0) return <span className='exercise-block-empty-hint'>Порожньо</span>;

    const available = bank.filter((w) => !built.includes(w.id));
    const pick = (id) => setBuilt((prev) => [...prev, id]);
    const remove = (id) => setBuilt((prev) => prev.filter((x) => x !== id));

    return (
        <div className='course-preview-sentence-order'>
            {cfg.prompt ? <p className='course-preview-question-text'>{cfg.prompt}</p> : ''}
            <div className='course-preview-sentence-build-area'>
                {built.length === 0 ? <span className='course-preview-sentence-placeholder'>Торкніться слів нижче, щоб скласти речення...</span> : ''}
                {built.map((id) => (
                    <span key={id} className='course-preview-word-chip built' onClick={() => remove(id)}>
                        {words.find((w) => w.id === id).text}
                    </span>
                ))}
            </div>
            <div className='course-preview-sentence-bank'>
                {available.map((w) => (
                    <span key={w.id} className='course-preview-word-chip' onClick={() => pick(w.id)}>{w.text}</span>
                ))}
            </div>
        </div>
    );
}

// Tap the word that's wrong - plain pick-state only (no reveal), same
// convention as every sibling here.
const ERROR_SPOTTING_DEFAULT = { words: [], errorWordIndex: 0 };
export function ErrorSpottingPreview({ block }) {
    const cfg = safeParseConfig(block.config, ERROR_SPOTTING_DEFAULT);
    const [picked, setPicked] = useState(null);

    if (!cfg.words || cfg.words.length === 0) return <span className='exercise-block-empty-hint'>Порожньо</span>;

    return (
        <div className='course-preview-error-spotting'>
            <p className='course-preview-question-text'>Знайдіть слово з помилкою:</p>
            <p className='course-preview-error-spotting-sentence'>
                {cfg.words.map((w, i) => (
                    <span key={i} className={'course-preview-error-word' + (picked === i ? ' picked' : '')}
                        onClick={() => setPicked(i)}>{w}</span>
                ))}
            </p>
        </div>
    );
}

// Drag-into-numbered-slots simulator lives in OrderingDnd.jsx (own file - it
// pulls in dnd-kit, which nothing else in this file needs).
export function OrderingPreview({ block }) {
    return <OrderingDnd block={block} />;
}

// Each new match gets its own color from this palette (shuffled once per
// game, cycling if there are more pairs than colors) instead of one shared
// green - with several lines crossing between two independently-shuffled
// columns, a single color makes it hard to tell which line belongs to
// which chip at a glance. Red, orange AND pink are deliberately excluded -
// anything in that warm red/pink/orange band reads as a caution/error tone
// in this app (the wrong-pick flash itself, IconDelete's fill), so a
// "correct" match rendered in one of those looks like something went wrong.
// Hues span 53°(yellow)-270°(purple), staying clear of the ~300-40° band.
const MATCH_COLOR_PALETTE = [
    { solid: '#30e846', bg: 'rgba(48, 232, 70, 0.14)', glow: 'rgba(48, 232, 70, 0.6)' },
    { solid: '#4f8ef7', bg: 'rgba(79, 142, 247, 0.14)', glow: 'rgba(79, 142, 247, 0.6)' },
    { solid: '#c084fc', bg: 'rgba(192, 132, 252, 0.14)', glow: 'rgba(192, 132, 252, 0.6)' },
    { solid: '#818cf8', bg: 'rgba(129, 140, 248, 0.14)', glow: 'rgba(129, 140, 248, 0.6)' },
    { solid: '#34d399', bg: 'rgba(52, 211, 153, 0.14)', glow: 'rgba(52, 211, 153, 0.6)' },
    { solid: '#f5d90a', bg: 'rgba(245, 217, 10, 0.14)', glow: 'rgba(245, 217, 10, 0.6)' },
    { solid: '#38bdf8', bg: 'rgba(56, 189, 248, 0.14)', glow: 'rgba(56, 189, 248, 0.6)' },
    { solid: '#a3e635', bg: 'rgba(163, 230, 53, 0.14)', glow: 'rgba(163, 230, 53, 0.6)' },
];

const GREEN_SOLIDS = ['#30e846', '#34d399', '#a3e635'];

// Draws an SVG line, in that pair's own color, between each matched pair's
// actual on-screen chip positions - recomputed whenever a match is made or
// the window resizes, since the two columns are independently ordered so a
// given pair's row heights can differ between the left and right side.
export function MatchingLines({ matched, matchColors, connections, containerRef, leftRefs, rightRefs }) {
    const [lines, setLines] = useState([]);

    useEffect(() => {
        const recompute = () => {
            const containerEl = containerRef.current;
            if (!containerEl) return;
            const containerRect = containerEl.getBoundingClientRect();
            // `connections` (deferred mode) can join ANY left to ANY right;
            // without it every entry of `matched` joins a pair id to itself.
            const conns = connections || (matched || []).map((id) => ({ key: id, left: id, right: id, color: matchColors[id] }));
            const next = conns.map((c) => {
                const leftEl = leftRefs.current[c.left];
                const rightEl = rightRefs.current[c.right];
                if (!leftEl || !rightEl) return null;
                const l = leftEl.getBoundingClientRect();
                const r = rightEl.getBoundingClientRect();
                return {
                    id: c.key,
                    x1: l.right - containerRect.left,
                    y1: l.top + l.height / 2 - containerRect.top,
                    x2: r.left - containerRect.left,
                    y2: r.top + r.height / 2 - containerRect.top,
                    color: (c.color || MATCH_COLOR_PALETTE[0]),
                };
            }).filter(Boolean);
            setLines(next);
        };
        recompute();
        window.addEventListener('resize', recompute);
        return () => window.removeEventListener('resize', recompute);
    }, [matched, matchColors, connections, containerRef, leftRefs, rightRefs]);

    return (
        <svg className='course-preview-matching-lines'>
            {lines.map((ln) => (
                <line key={ln.id} x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2}
                    style={{ stroke: ln.color.solid, filter: 'drop-shadow(0 0 3px ' + ln.color.glow + ')' }} />
            ))}
        </svg>
    );
}

// One chip's content - plain text as before, or a media element for the
// image/audio/video kinds (see matchingConfig.js). Audio/video get their
// own native `controls`, so a click on THOSE specifically must not bubble
// up to the chip's own onClick (which selects the chip for matching) -
// otherwise pressing play would also register as a match attempt. Text and
// image chips have no such conflict, nothing special needed there.
export function MatchingChipContent({ side, mediaWidth, mediaHeight }) {
    if (side.kind === MATCHING_SIDE_KIND_TEXT) return side.value;
    if (side.kind === 'image') return <img className='course-preview-matching-media' src={side.value} alt='' style={{ width: mediaWidth, height: mediaHeight }} />;
    if (side.kind === 'audio') return <audio className='course-preview-matching-media' src={side.value} controls onClick={(e) => e.stopPropagation()} style={{ width: mediaWidth }} />;
    return <video className='course-preview-matching-media' src={side.value} controls onClick={(e) => e.stopPropagation()} style={{ width: mediaWidth, height: mediaHeight }} />;
}

// Tap a left item, then its match on the right - both columns shuffled,
// with no row ever having its correct match directly across from it (see
// rotateNoFixedPoints - a plain independent shuffle of both sides can
// coincidentally align a pair, which gives the answer away for free and
// defeats the point of the exercise). A correct pick locks both sides in a
// shared color and draws a connecting line between them (MatchingLines); a
// wrong pick briefly flashes red and clears the selection. Either side of
// a pair can be text, image, audio or video, any combination - see
// matchingConfig.js, shared with the builder editor so the two can't drift.
export function MatchingPreview({ block }) {
    const cfg = safeParseConfig(block.config, MATCHING_DEFAULT_CONFIG);
    const mediaWidth = cfg.mediaWidth || MATCHING_DEFAULT_CONFIG.mediaWidth;
    const mediaHeight = cfg.mediaHeight || MATCHING_DEFAULT_CONFIG.mediaHeight;
    const pairs = useMemo(() => normalizePairs(cfg.pairs)
        .map((p, i) => ({ ...p, id: i }))
        .filter((p) => isSideFilled(p.left) || isSideFilled(p.right)), [cfg.pairs]);
    const [orders] = useState(() => {
        const left = shuffle(pairs);
        return { left, right: rotateNoFixedPoints(left) };
    });
    const { left: leftOrder, right: rightOrder } = orders;
    // deferred mode never colors a connection green - green means "correct"
    // once "Перевірити" reveals it, so before that the palette avoids it.
    const [colorQueue] = useState(() => shuffle(getMatchingMode(cfg) === MATCHING_MODE_DEFERRED ? MATCH_COLOR_PALETTE.filter((c) => !GREEN_SOLIDS.includes(c.solid)) : MATCH_COLOR_PALETTE));
    const [selectedLeft, setSelectedLeft] = useState(null);
    const [matched, setMatched] = useState([]);
    const [matchColors, setMatchColors] = useState({});
    const [wrongId, setWrongId] = useState(null);
    const deferred = getMatchingMode(cfg) === MATCHING_MODE_DEFERRED;
    const [connections, setConnections] = useState([]);
    const containerRef = useRef(null);
    const leftRefs = useRef({});
    const rightRefs = useRef({});

    if (pairs.length === 0) return <span className='exercise-block-empty-hint'>Порожньо</span>;

    // deferred: any left may be joined to any right and nothing is judged
    // here - tap an already-joined chip to undo its connection. Colors are
    // handed out from the shuffled queue, skipping ones in use.
    const connectionOf = (side, id) => connections.find((c) => c[side] === id);
    const pickLeftDeferred = (pair) => {
        if (connectionOf('left', pair.id)) {
            setConnections((prev) => prev.filter((c) => c.left !== pair.id));
            return;
        }
        setSelectedLeft(pair.id);
    };
    const pickRightDeferred = (pair) => {
        if (selectedLeft == null) {
            if (connectionOf('right', pair.id)) setConnections((prev) => prev.filter((c) => c.right !== pair.id));
            return;
        }
        setConnections((prev) => {
            const kept = prev.filter((c) => c.right !== pair.id);
            const used = kept.map((c) => c.color);
            const color = colorQueue.find((c) => !used.includes(c)) || colorQueue[kept.length % colorQueue.length];
            return [...kept, { key: selectedLeft + '-' + pair.id, left: selectedLeft, right: pair.id, color }];
        });
        setSelectedLeft(null);
    };
    const deferredStyle = (side, id) => {
        const c = connectionOf(side, id);
        return c ? { '--match-solid': c.color.solid, '--match-bg': c.color.bg } : undefined;
    };

    const pickLeft = (pair) => {
        if (deferred) return pickLeftDeferred(pair);
        if (matched.includes(pair.id)) return;
        setSelectedLeft(pair.id);
    };
    const pickRight = (pair) => {
        if (deferred) return pickRightDeferred(pair);
        if (selectedLeft == null || matched.includes(pair.id)) return;
        if (pair.id === selectedLeft) {
            const color = colorQueue[matched.length % colorQueue.length];
            setMatchColors((prev) => ({ ...prev, [selectedLeft]: color }));
            setMatched((prev) => [...prev, selectedLeft]);
            setSelectedLeft(null);
        } else {
            setWrongId(pair.id);
            setSelectedLeft(null);
            setTimeout(() => setWrongId(null), 400);
        }
    };
    const chipStyle = (id) => {
        const color = matchColors[id];
        return color ? { '--match-solid': color.solid, '--match-bg': color.bg } : undefined;
    };

    return (
        <div className='course-preview-matching-game' ref={containerRef}>
            <MatchingLines matched={matched} matchColors={matchColors} connections={deferred ? connections : undefined} containerRef={containerRef} leftRefs={leftRefs} rightRefs={rightRefs} />
            <div className='course-preview-matching-game-col'>
                {leftOrder.map((p) => (
                    <div key={p.id} ref={(el) => { leftRefs.current[p.id] = el; }} onClick={() => pickLeft(p)} style={deferred ? deferredStyle('left', p.id) : chipStyle(p.id)}
                        className={'course-preview-matching-chip' + (selectedLeft === p.id ? ' selected' : '') + (deferred ? (connectionOf('left', p.id) ? ' matched connected' : '') : (matched.includes(p.id) ? ' matched' : ''))}>
                        <MatchingChipContent side={p.left} mediaWidth={mediaWidth} mediaHeight={mediaHeight} />
                    </div>
                ))}
            </div>
            <div className='course-preview-matching-game-col'>
                {rightOrder.map((p) => (
                    <div key={p.id} ref={(el) => { rightRefs.current[p.id] = el; }} onClick={() => pickRight(p)} style={deferred ? deferredStyle('right', p.id) : chipStyle(p.id)}
                        className={'course-preview-matching-chip' + (deferred ? (connectionOf('right', p.id) ? ' matched connected' : '') : (matched.includes(p.id) ? ' matched' : '')) + (wrongId === p.id ? ' wrong' : '')}>
                        <MatchingChipContent side={p.right} mediaWidth={mediaWidth} mediaHeight={mediaHeight} />
                    </div>
                ))}
            </div>
        </div>
    );
}
